import { useCallback, useState } from 'react'
import { apiFetch } from '../../api/core'
import type { FormState, Template } from '../../types/testdata.types'

/** Opgeslagen sjablonen (CRUD tegen `/api/testdata/templates`). Het toepassen
 * van een sjabloon op de huidige form/lines/docType-state is cross-cutting en
 * blijft daarom in de hoofdhook (`loadTemplate`) — `applyTemplateToForm` hier
 * is de pure vertaalslag ernaartoe. */
export function useTemplates() {
  const [templates, setTemplates] = useState<Template[]>([])
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const loadTemplates = useCallback(async () => {
    try {
      const data = await apiFetch<Template[]>('/api/testdata/templates')
      setTemplates(data)
    } catch { /* niet kritisch */ }
  }, [])

  const deleteTemplate = async (id: number) => {
    setDeletingId(id)
    try {
      await apiFetch(`/api/testdata/templates/${id}`, { method: 'DELETE' })
      setTemplates(ts => ts.filter(t => t.id !== id))
    } catch (e: unknown) {
      throw e instanceof Error ? e : new Error('Verwijderen mislukt')
    } finally {
      setDeletingId(null)
    }
  }

  return { templates, deletingId, loadTemplates, deleteTemplate }
}

export type TemplatesState = ReturnType<typeof useTemplates>

/** Vertaalt een opgeslagen sjabloon naar een form-patch (partijvelden). */
export function applyTemplateToForm(tpl: Template, form: FormState): FormState {
  return {
    ...form,
    supplier_name:    tpl.supplier_name ?? '',
    supplier_gln:     tpl.supplier_gln ?? '',
    supplier_iban:    tpl.supplier_iban ?? '',
    supplier_vat:     tpl.supplier_vat ?? '',
    supplier_kvk:     tpl.supplier_kvk ?? '',
    supplier_attn:    tpl.supplier_attn ?? '',
    supplier_address: tpl.supplier_address ?? '',
    supplier_postal:  tpl.supplier_postal ?? '',
    supplier_city:    tpl.supplier_city ?? '',
    supplier_phone:   tpl.supplier_phone ?? '',
    supplier_email:   tpl.supplier_email ?? '',
    supplier_website: tpl.supplier_website ?? '',
    buyer_name:       tpl.buyer_name ?? '',
    buyer_gln:        tpl.buyer_gln ?? '',
    buyer_vat:        tpl.buyer_vat ?? '',
    buyer_attn:       tpl.buyer_attn ?? '',
    buyer_address:    tpl.buyer_address ?? '',
    buyer_postal:     tpl.buyer_postal ?? '',
    buyer_city:       tpl.buyer_city ?? '',
    delivery_party_name: tpl.delivery_party_name ?? '',
    delivery_party_gln:  tpl.delivery_party_gln ?? '',
  }
}
